import {
  IActivityNode,
  IAttachment,
  IAttachmentsListNode,
  ICourseNode,
  IDuration,
  IInlineNode,
  ILessonPlan,
  INodeList,
  INoteData,
  INoteNode,
  IQuestionNode,
  ISimplifiedNode,
  IUnitNode,
} from '../components/ingestion/IngestedTree'
import {
  AssignmentType,
  GradingType,
  NodeType,
  QuestionType,
} from 'shared/types/ingestion'
import mime from 'mime'
import {
  IQuestionDataBase,
  IQuestionShortResponse,
} from '../components/ingestion/IngestionQuestions'
import { PARAGRAPH_SPACING, wrapB, wrapP } from './html_utility'
import { INodeWithNodes } from './types'
import { parseHtmlString as parseHtmlStringUtil } from 'shared/utils'

export function convertParagraphSpacing(content?: string) {
  if (!content) return ''
  return content.replaceAll('\n\n', PARAGRAPH_SPACING)
}

export function parseHtmlString(htmlString?: string) {
  return parseHtmlStringUtil(convertParagraphSpacing(htmlString))
}

export function createNoteNode({ value, ...rest }: INoteData): INoteNode {
  return {
    type: NodeType.Note,
    data: {
      ...rest,
      value: value.replaceAll('\n\n', PARAGRAPH_SPACING),
    },
  }
}

export function createMultiPartQuestion(
  questionText: string,
  questions: (IQuestionNode | INoteNode)[],
): IInlineNode {
  return {
    type: NodeType.Inline,
    data: {
      title: 'Multipart Question',
      studentFacing: true,
      type: NodeType.MultiPartQuestion,
    },
    nodes: [
      createNoteNode({
        title: 'Description',
        value: questionText,
        studentFacing: true,
      }),
      ...questions,
    ],
  }
}

export function addChildNode({
  parent,
  child,
}: {
  parent: INodeWithNodes
  child: ISimplifiedNode
}) {
  if (!parent.nodes) {
    parent.nodes = []
  }
  parent.nodes.push(child)
}

export function createLongCardListType({
  nodes,
  title,
}: {
  nodes: ISimplifiedNode[]
  title: string
}): INodeList {
  return {
    type: NodeType.NodeList,
    data: {
      title,
    },
    nodes,
  }
}

export function createLessonPlan(
  data: {
    title: string
    duration?: IDuration
  },
  nodes: ISimplifiedNode[],
): ILessonPlan {
  return {
    type: NodeType.LessonPlan,
    data,
    nodes,
  }
}

export function createAttachmentsList(data: {
  title: string
  studentFacing?: boolean
  ExternalID?: string
  attachments_list: IAttachment[]
}): IAttachmentsListNode {
  return {
    type: NodeType.AttachmentsList,
    data: {
      studentFacing: false,
      ...data,
    },
  }
}

export function createAttachment({
  name,
  url,
}: {
  name: string
  url: string
}): IAttachment {
  return {
    name,
    url,
    type: mime.getType(url) ?? undefined,
  }
}

export function createNodeList<T = ISimplifiedNode>(
  data: { title: string },
  nodes: T[],
): INodeList<T> {
  return {
    type: NodeType.NodeList,
    data,
    nodes,
  }
}

export function createActivity(
  {
    title,
    isAssignable,
    assignment,
    assessment_grading,
    duration,
    points,
  }: {
    title: string
    isAssignable?: boolean
    points?: number
    assignment?: AssignmentType
    assessment_grading?: GradingType
    duration?: IDuration
  },
  nodes: ISimplifiedNode[],
): IActivityNode {
  return {
    type: NodeType.Activity,
    data: {
      title,
      duration,
      isAssignable: isAssignable === undefined ? true : isAssignable,
      assignment: assignment || 'participation',
      assessment_grading: assessment_grading || 'formative',
      points: points || 1,
    },
    nodes,
  }
}

export function addUnitsToCourse(
  course: ICourseNode,
  units: IUnitNode[],
  title: string = 'Units',
) {
  const nodeList: INodeList = {
    type: NodeType.NodeList,
    data: { title },
    nodes: units,
  }
  addChildNode({
    parent: course,
    child: nodeList,
  })
}

function formatQuestionData<T>({
  description,
  teacherNote,
  ...rest
}: Partial<IQuestionDataBase> & { description: string } & T) {
  const toReplace = [
    ['\n\n', PARAGRAPH_SPACING],
    ['\n', ''],
    ['\t', ''],
  ]
  return {
    points: 4,
    ...rest,
    description: toReplace.reduce(
      (acc, [r, w]) => acc.replaceAll(r, w),
      description,
    ),
    teacherNote: toReplace.reduce(
      (acc, [r, w]) => acc.replaceAll(r, w),
      teacherNote || '',
    ),
  }
}

export function createShortResponse(
  data: Partial<IQuestionDataBase> & { description: string },
): IQuestionShortResponse {
  return {
    type: NodeType.Question,
    data: {
      title: data.title || 'Question',
      studentFacing: true,
      ...formatQuestionData(data),
      type: QuestionType.ShortResponse,
    },
  }
}
